import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Alert,
  TextFilter,
  ExpandableSection,
  Pagination,
  Checkbox,
  ProgressBar
} from '@awsui/components-react';
import { useWebSocketTrends } from './websocket-client';

// Helper function to get cookie value
const getCookie = (name) => {
  const cookieName = name + "=";
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return "";
};

export function TrendsPanel() {
  // WebSocket URL - replace with your actual WebSocket API Gateway URL
  const WEBSOCKET_URL = 'wss://puc3yyqkfj.execute-api.us-east-1.amazonaws.com/prod/';
  
  const [filteredItems, setFilteredItems] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrends, setSelectedTrends] = useState({});
  const [validationSuccess, setValidationSuccess] = useState(false);
  const itemsPerPage = 5;

  // Get access token
  const accessToken = getCookie('accessToken');
  
  // Debug logging
  console.log('TrendsPanel - WebSocket URL:', WEBSOCKET_URL);
  console.log('TrendsPanel - Access token available:', !!accessToken);
  console.log('TrendsPanel - Access token length:', accessToken?.length || 0);
  
  // Use WebSocket hook
  const {
    isConnected,
    status,
    result,
    partialResults,
    agentCoreStreams,
    error,
    isProcessing,
    processTrends,
    validateTrends
  } = useWebSocketTrends(WEBSOCKET_URL, accessToken);

  // Get access token for API calls (keeping for compatibility)
  const getAccessToken = () => {
    const token = getCookie('accessToken');
    if (!token) {
      throw new Error('No access token found. Please log in again.');
    }
    return token;
  };

  // Handle WebSocket result and AgentCore streams
  useEffect(() => {
    if (result) {
      console.log('Received result from WebSocket:', result);
      
      // Extract trends from result (adjust based on your agent's response format)
      let trends = [];
      if (result.trends) {
        trends = result.trends;
      } else if (result.stored_trends) {
        trends = result.stored_trends;
      } else if (Array.isArray(result)) {
        trends = result;
      } else if (result.data && Array.isArray(result.data)) {
        trends = result.data;
      }
      
      console.log('Extracted trends:', trends);
      console.log('First trend structure:', trends[0]);
      setFilteredItems(trends || []);
    }
  }, [result]);

  // Handle AgentCore streaming data
  useEffect(() => {
    if (agentCoreStreams.length > 0) {
      console.log('Received AgentCore streams:', agentCoreStreams);
      
      // Process the latest stream data
      const latestStream = agentCoreStreams[agentCoreStreams.length - 1];
      
      // Try to extract trends from the stream data
      if (latestStream.data) {
        let trends = [];
        const streamData = latestStream.data;
        
        if (streamData.trends) {
          trends = streamData.trends;
        } else if (streamData.stored_trends) {
          trends = streamData.stored_trends;
        } else if (Array.isArray(streamData)) {
          trends = streamData;
        } else if (streamData.data && Array.isArray(streamData.data)) {
          trends = streamData.data;
        }
        
        if (trends.length > 0) {
          console.log('Extracted trends from AgentCore stream:', trends);
          console.log('First stream trend structure:', trends[0]);
          setFilteredItems(trends);
        }
      }
    }
  }, [agentCoreStreams]);



  // Memoize filtered data
  const filteredData = React.useMemo(() => {
    if (!filterText || filterText.trim() === '') {
      return filteredItems;
    }
    
    const lowerFilterText = filterText.toLowerCase();
    return filteredItems.filter(item => 
      (item.trend_id && item.trend_id.toLowerCase().includes(lowerFilterText)) ||
      (item.trend_name && item.trend_name.toLowerCase().includes(lowerFilterText)) ||
      (item.description && item.description.toLowerCase().includes(lowerFilterText)) ||
      (item.strategic_significance && item.strategic_significance.toLowerCase().includes(lowerFilterText)) ||
      (item.implications && item.implications.toLowerCase().includes(lowerFilterText))
    );
  }, [filteredItems, filterText]);

  // Calculate pagination
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  // Handle processing trends via WebSocket
  const handleProcessTrends = () => {
    if (!isConnected) {
      alert('WebSocket not connected. Please refresh the page.');
      return;
    }
    
    processTrends("Process trends for competitive intelligence analysis");
  };

  // Get status indicator type for WebSocket status
  const getStatusIndicatorType = () => {
    if (status && (status.includes('Error') || error)) return 'error';
    if (status && status.includes('Completed')) return 'success';
    if (status && (status.includes('processing') || status.includes('calling') || isProcessing)) return 'in-progress';
    return 'pending';
  };

  const handleValidateTrends = () => {
    const trendsToStore = filteredItems.filter(item => selectedTrends[item.trend_title]);
    
    if (trendsToStore.length === 0) {
      alert('Please select at least one trend to validate.');
      return;
    }
    
    if (!isConnected) {
      alert('WebSocket not connected. Please refresh the page.');
      return;
    }
    
    setValidationSuccess(false);
    validateTrends(trendsToStore);
  };

  const handleCheckboxChange = (trendId, checked) => {
    setSelectedTrends(prev => ({
      ...prev,
      [trendId]: checked
    }));
  };

  const handleSelectAll = (select) => {
    const newSelectedTrends = {};
    filteredData.forEach(item => {
      newSelectedTrends[item.trend_id] = select;
    });
    setSelectedTrends(newSelectedTrends);
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h2">Get Compete Trends (WebSocket)</Header>
        
        {/* Connection Status */}
        <Box padding="s" variant="awsui-key-label">
          <StatusIndicator type={isConnected ? 'success' : 'error'}>
            WebSocket {isConnected ? 'Connected' : 'Disconnected'}
          </StatusIndicator>
        </Box>
        
        <Box padding="l" variant="awsui-key-label">
          <Button
            variant="primary"
            onClick={handleProcessTrends}
            disabled={!isConnected || isProcessing}
          >
            Process Trends
          </Button>
        </Box>
        
        {/* Status Display */}
        {status && (
          <Box padding="l" variant="awsui-key-label">
            <SpaceBetween size="m">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type={getStatusIndicatorType()}>
                  {status}
                </StatusIndicator>
              </div>
              
              {isProcessing && (
                <ProgressBar
                  status="in-progress"
                  value={
                    agentCoreStreams.length > 0 ? Math.min(90, agentCoreStreams.length * 5) :
                    partialResults.length > 0 ? Math.min(90, partialResults.length * 10) : 30
                  }
                  additionalInfo={
                    agentCoreStreams.length > 0 ? 
                      `AgentCore streaming... (${agentCoreStreams.length} streams received)` :
                    partialResults.length > 0 ? 
                      `Streaming data... (${partialResults.length} chunks received)` : 
                      "Processing trends..."
                  }
                />
              )}
              
              {/* Show AgentCore streaming progress */}
              {agentCoreStreams.length > 0 && (
                <Box>
                  <Box variant="awsui-key-label">AgentCore Streaming Progress</Box>
                  <div>{agentCoreStreams.length} stream messages received directly from AgentCore</div>
                  {agentCoreStreams.length > 0 && (
                    <div>Latest stream type: {agentCoreStreams[agentCoreStreams.length - 1].streamType}</div>
                  )}
                </Box>
              )}
              
              {/* Show partial results count */}
              {partialResults.length > 0 && (
                <Box>
                  <Box variant="awsui-key-label">Lambda Streaming Progress</Box>
                  <div>{partialResults.length} data chunks received from Lambda</div>
                </Box>
              )}
            </SpaceBetween>
          </Box>
        )}
        
        {error && (
          <Alert type="error" header="Error">
            {error}
          </Alert>
        )}
        
        {/* Results Display */}
        {filteredItems.length > 0 && (
          <Box padding="l" variant="awsui-key-label">
            <SpaceBetween size="l">
              <div>
                <Box variant="awsui-key-label">
                    <SpaceBetween size="s" direction="horizontal">
                      <span>Trends ({filteredData.length})</span>
                      <TextFilter
                        filteringPlaceholder="Find trends"
                        filteringText={filterText}
                        onChange={({ detail }) => {
                          setFilterText(detail.filteringText);
                          setCurrentPage(1);
                        }}
                      />
                      <Button onClick={() => handleSelectAll(true)}>Select All</Button>
                      <Button onClick={() => handleSelectAll(false)}>Deselect All</Button>
                    </SpaceBetween>
                  </Box>
                  
                  {filteredData.length === 0 ? (
                    <Box textAlign="center" padding="l">
                      <b>No trends found</b>
                      <Box padding={{ bottom: 's' }}>
                        No trends match the current filter criteria.
                      </Box>
                    </Box>
                  ) : (
                    <SpaceBetween size="l">
                      {paginatedData.map((trend, index) => (
                        <ExpandableSection
                          key={trend.trend_title || index}
                          headerText={
                            <SpaceBetween size="s" direction="horizontal">
                              <Checkbox
                                checked={selectedTrends[trend.trend_title] || false}
                                onChange={({ detail }) => handleCheckboxChange(trend.trend_title, detail.checked)}
                              />
                              <span>{trend.trend_title || 'Unnamed Trend'}</span>
                            </SpaceBetween>
                          }
                          variant="container"
                        >
                          <SpaceBetween size="m">
                            <Box>
                              <Box variant="awsui-key-label">Description</Box>
                              <div>{trend.trend_description || 'No description available'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Primary Competitor</Box>
                              <div>{trend.primary_competitor || 'No competitor specified'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Customer Names</Box>
                              <div>{Array.isArray(trend.customer_names) ? 
                                trend.customer_names.join(', ') : 
                                'No customers listed'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Opportunity IDs</Box>
                              <div>{Array.isArray(trend.opportunity_ids) ? 
                                trend.opportunity_ids.join(', ') : 
                                'No opportunities listed'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Insight IDs</Box>
                              <div>{Array.isArray(trend.insight_id) ? 
                                trend.insight_id.join(', ') : 
                                'No insights listed'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Quality Score</Box>
                              <div>{trend.quality_score || 'No quality score available'}</div>
                            </Box>
                            

                            
                            <Box>
                              <Box variant="awsui-key-label">Trend Validation Explanation</Box>
                              <div>{trend.trend_validation_explanation || 'No validation explanation available'}</div>
                            </Box>
                                                        
                            <Box>
                              <Box variant="awsui-key-label">Executive Summary</Box>
                              <div>{trend.executive_summary || 'No executive summary available'}</div>
                            </Box>
                            
                            <Box>
                              <Box variant="awsui-key-label">Field Summary</Box>
                              <div>{trend.field_summary || 'No field summary available'}</div>
                            </Box>
                          </SpaceBetween>
                        </ExpandableSection>
                      ))}
                      
                      {filteredData.length > itemsPerPage && (
                        <Pagination
                          currentPageIndex={currentPage}
                          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                          pagesCount={Math.ceil(filteredData.length / itemsPerPage)}
                        />
                      )}
                      
                      {filteredData.length > 0 && (
                        <Box textAlign="right">
                          <Button
                            variant="primary"
                            onClick={handleValidateTrends}
                            disabled={!isConnected || isProcessing}
                          >
                            Validate Selected Trends
                          </Button>
                        </Box>
                      )}
                    </SpaceBetween>
                  )}
                </div>
            </SpaceBetween>
          </Box>
        )}
        
        {validationSuccess && (
          <Alert type="success" header="Success">
            Trends validated successfully!
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
}
